import { useTranslation } from 'react-i18next'
import Tippy from '@tippyjs/react'

import Icon from '../../../../ui/Icon'
import { CourtSvg } from '../../../../ui/courtSvg/CourtSvg'
import { Button } from '../../../../buttons/Button'

import {
  AvailableStart,
  CourtAvailability,
} from '../../../../../types/booking/booking'
import { BlockReason, CourtType } from '../../../../../types/court/court'
import { formatMinutes, formatMoney, timeOf } from '../../../../../utils/booking/booking'

export type GridStart = {
  startTime: string
  endTime: string
  /** The server offers this start for the selected duration. */
  available: boolean
  price?: AvailableStart['price']
  /** Why an unavailable start is not offered, when a court block covers it. */
  blockReason?: BlockReason
  /** Unavailable because a booking waiting for its payment covers it. */
  held?: boolean
  /** Not offered only because the session would run into a booking or block later on (the start itself is free). */
  overlaps?: boolean
}

type CourtStartGridProps = {
  court: CourtAvailability
  clubName?: string
  durationMinutes: number
  starts: GridStart[]
  showPrices?: boolean
  disabled?: boolean
  isSelected: (start: AvailableStart) => boolean
  /** Inside a picked session: shown as part of it. */
  isCovered: (start: AvailableStart) => boolean
  /** Can still be clicked while covered (single pick: replaces the pick). */
  coveredSelectable?: boolean
  /** Earlier start that would run into a picked session. */
  isBlocked: (start: AvailableStart) => boolean
  /** Covered by the customer's prepaid minutes (labelled, keeps the normal colour). */
  isIncluded?: (start: AvailableStart) => boolean
  onToggle: (start: AvailableStart) => void
}

/**
 * Start times of one court for the selected duration. Starts the server did not offer are shown disabled: "Booked",
 * "On hold" (a booking in checkout) or the block reason when the start itself is occupied, "Overlaps" when it is free
 * but the session would run into a booking or block. A pick is shown in the selection colour, the later starts it covers
 * faded in that colour (an "Overlaps" one without its label while covered), and earlier starts that would run into it
 * faded and disabled. "Overlaps" and "On hold"
 * starts carry an info icon whose tooltip explains why they cannot be picked.
 */
const CourtStartGrid = ({
  court,
  clubName,
  durationMinutes,
  starts,
  showPrices = true,
  disabled = false,
  isSelected,
  isCovered,
  coveredSelectable = false,
  isBlocked,
  isIncluded,
  onToggle,
}: CourtStartGridProps) => {
  const { t } = useTranslation(['bookings', 'courts', 'blocks'])
  const isIndoor = court.courtType === CourtType.INDOOR
  const courtType = t(`courts:courts.type.${court.courtType}`)
  const details = [courtType, clubName, formatMinutes(durationMinutes)].filter(Boolean)

  return (
    <section className="ph-card court-start-grid">
      <header className="header">
        <div className="details">
          <h3>{court.courtName}</h3>
          <div className="meta">{details.join(' · ')}</div>
        </div>
        <Tippy content={courtType} placement="top" className="ph-tooltip">
          <div className={`court-preview ${isIndoor ? 'indoor' : 'outdoor'}`}>
            <CourtSvg indoor={isIndoor} w={40} h={70} />
          </div>
        </Tippy>
      </header>

      <div className="starts">
        {starts.length === 0 && (
          <div className="empty-message">{t('bookings:booking.session-picker.no-starts')}</div>
        )}
        {starts.map(start => {
          const selected = start.available && isSelected(start)
          const covered = !selected && isCovered(start)
          const runsInto = !start.available && !covered && Boolean(start.overlaps)
          const unavailable = !start.available && !covered && !runsInto
          const blocked = start.available && !selected && !covered && isBlocked(start)
          const time = timeOf(start.startTime)
          // A pick only changes how the starts look (selection colour, faded); a covered "Overlaps" start is part of the
          // pick, so it loses its label until the pick is removed.
          const state = covered && !start.available && start.overlaps
            ? ''
            : start.available
            ? isIncluded?.(start)
              ? t('bookings:booking.booking-courts.prepaid-short')
              : showPrices && start.price
                ? formatMoney(start.price)
                : t('bookings:booking.booking-courts.available')
            : start.overlaps
              ? t('bookings:booking.session-picker.overlaps')
              : start.blockReason
                ? t(`blocks:blocks.reasons.${start.blockReason}`)
                : start.held
                  ? t('bookings:booking.booking-courts.held')
                  : t('bookings:booking.booking-courts.booked')
          const classes = [
            'start',
            selected && 'selected',
            covered && 'selected covered',
            unavailable && (start.blockReason ? 'booked closed' : start.held ? 'booked held' : 'booked'),
            (blocked || runsInto) && 'overlapping',
          ].filter(Boolean)
          const held = unavailable && Boolean(start.held) && !start.blockReason
          const stateLabel =
            selected || (covered && !state)
              ? [t('bookings:booking.booking-courts.selected'), state].filter(Boolean).join(', ')
              : state
          const hint = runsInto
            ? t('bookings:booking.session-picker.overlaps-hint', {
                time,
                duration: formatMinutes(durationMinutes),
              })
            : held
              ? t('bookings:booking.booking-courts.held-hint')
              : undefined

          const button = (
            <Button
              key={start.startTime}
              variant="ghost"
              className={classes.join(' ')}
              disabled={
                disabled ||
                unavailable ||
                runsInto ||
                blocked ||
                (covered && (!coveredSelectable || !start.available))
              }
              onClick={() => onToggle(start)}
              aria-pressed={selected}
              aria-label={[time, stateLabel, hint].filter(Boolean).join(', ')}
              icon={<span className="time">{time}</span>}
              // A blank label keeps the card's height.
              label={state || '\u00a0'}
              labelClassName="state"
            />
          )

          if (!hint) {
            return button
          }

          // A disabled button fires no mouse events, so the tooltip hangs on a wrapper around it.
          return (
            <Tippy
              key={start.startTime}
              content={hint}
              placement="top"
              className="ph-tooltip"
              delay={[200, 0]}
            >
              <span className="start-hint" tabIndex={0} aria-label={hint}>
                {button}
                <Icon name="info" size={14} className="start-hint-icon" aria-hidden />
              </span>
            </Tippy>
          )
        })}
      </div>
    </section>
  )
}

export default CourtStartGrid
